/* I'm going to violate my own best practice of only thinly wrapping a 3rd party API. Reasons:
   - ClipperLib follows an OO design that won't work well with the guarded call pattern.
   - The setup for calling the boolean operations in ClipperLib just BEGS to be wrapped in a more
     functional style.

  ClipperLib docs:
  https://github.com/junmer/clipper-lib/blob/master/Documentation.md */

import ClipperLib from 'clipper-lib';

import { clippingLibrary } from 'geometry/geojson/constants';

import { workerLogError } from 'scripts/editZones.worker';
import { logApiError } from './guarded-call';
import { isValidXPolygon } from 'geometry/geojson/validator';
import { calculatePolygonRingWindingFix } from 'geometry/geojson/winding';
import { doesXPolygonHaveArea } from 'geometry/geojson/area';
import { polygonArrayToXPolygon } from 'geometry/geojson/convert';
import { convertCoordinatesBackToFloats, convertCoordinatesToInts } from 'geometry/geojson/round';

export const library = clippingLibrary.CLIPPER;

/* eslint-disable new-cap */ // ClipperLib disagrees with eslint re the initial caps.

const clipper = new ClipperLib.Clipper();

const _xPolygonToClipperPaths = (xPolygon) => {
  let paths = [];
  const multiPolygonCoords = (xPolygon.type === 'Polygon') ? [xPolygon.coordinates] : xPolygon.coordinates;
  multiPolygonCoords.forEach(polygonCoords => {
    paths = paths.concat(polygonCoords.map((ring, ringI) => {
      const fixedRing = calculatePolygonRingWindingFix(ring, ringI, true);
      const useRing = fixedRing || ring;
      return useRing.map( vertex => ({X:vertex[0],Y:vertex[1]}) );
    }));
  });
  return paths;
};

const _clipperPathToRing = (path) => {
  const ring = path.map(vertex => [vertex.X, vertex.Y]);
  if (path[0].X !== path[path.length-1].X || path[0].Y !== path[path.length-1].Y) {
    ring.push(ring[0]); // Add closing vertex.
  }
  return ring;
};

/** Called recursively from 2nd-level polytree nodes and down to process all nodes of polytree.
 *  @param node Polytree node, 2nd-level or lower.
 *  @return Array of Polygons found at this node and recursed children.
 */
const _polyTreeNodeToPolygonArray = (node) => {
  let polygons = [];

  // If this node is not a hole, create a Polygon from it and any hole children.
  const children = node.Childs();
  if (!node.IsHole()) {
    const polygon = {type:'Polygon', coordinates:[_clipperPathToRing(node.Contour())]};
    children.forEach(child => {
      if (child.IsHole()) {
        polygon.coordinates.push(_clipperPathToRing(child.Contour()));
      }
    });
    polygons.push(polygon);
  }

  /* Recurse into any child nodes. Note that holes can contain non-hole nodes, which in the GeoJson format
     must be represented as separate Polygons. */
  children.forEach(child => {
    polygons = polygons.concat(_polyTreeNodeToPolygonArray(child));
  });

  return polygons;
};

/** Called recursively to process all nodes of polytree.
 *  @param  node The polytree instance (same thing as top-level node) or a child node.
 *  @return Array of Polygons found at this node and recursed children.
 */
const _polyTreeToXPolygon = (polytree) => {
  let polygons = [];
  const children = polytree.Childs(); // eslint-disable-line new-cap
  children.forEach(node => {
    polygons = polygons.concat(_polyTreeNodeToPolygonArray(node));
  });

  return polygons.length > 0 ? polygonArrayToXPolygon(polygons) : null;
};
const clipTypeToFuncName = {
  [ClipperLib.ClipType.ctIntersection]: 'intersect',
  [ClipperLib.ClipType.ctDifference]: 'difference',
  [ClipperLib.ClipType.ctUnion]: 'union'
};

const _executeBooleanOperation = ({ xPolygon1, xPolygon2, clipType }) => {
  const funcName = clipTypeToFuncName[clipType] || 'unknown function';
  const { decimalPlaces, intCoordinates } = convertCoordinatesToInts([xPolygon1.coordinates, xPolygon2.coordinates]);
  let intXPolygon1 = { ...xPolygon1, coordinates: intCoordinates[0] };
  let intXPolygon2 = { ...xPolygon2, coordinates: intCoordinates[1] };

  // Handle cases where converting coords to integers simplified a polygon to a no-area shape.
  if (!doesXPolygonHaveArea(intXPolygon1)) {
    if (clipType === ClipperLib.ClipType.ctIntersection || clipType === ClipperLib.ClipType.ctDifference)
    { return null; }
    if (clipType === ClipperLib.ClipType.ctUnion) { return xPolygon2; }
  }
  if (!doesXPolygonHaveArea(intXPolygon2)) {
    if (clipType === ClipperLib.ClipType.ctIntersection) { return null; }
    if (clipType === ClipperLib.ClipType.ctDifference || clipType === ClipperLib.ClipType.ctUnion) { return xPolygon1; }
  }

  try {
    const subjectPaths = _xPolygonToClipperPaths(intXPolygon1);
    const clippingPaths = _xPolygonToClipperPaths(intXPolygon2);
    let solution;
    try {
      clipper.Clear(); // Clear subject and clipping polygons from previous operations.
      if (!clipper.AddPaths(subjectPaths, ClipperLib.PolyType.ptSubject, true))
      { throw new Error('Failed to add paths for intXPolygon1'); }
      if (!clipper.AddPaths(clippingPaths, ClipperLib.PolyType.ptClip, true))
      { throw new Error('Failed to add paths for intXPolygon2'); }
      solution = new ClipperLib.PolyTree();
      if (!clipper.Execute(clipType, solution, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero))
      { throw new Error('clipper.execute failed.'); }
    } catch(err) {
      logApiError({args:[intXPolygon1,intXPolygon2], funcName, library, errorMessage:err.message});
      return undefined;
    }

    const xPolygon = _polyTreeToXPolygon(solution);
    if (xPolygon === null || !doesXPolygonHaveArea(xPolygon)) { return null; } // Empty shape returned--not an error.

    const finalXPolygon = {
      ...xPolygon,
      coordinates: convertCoordinatesBackToFloats(xPolygon.coordinates, decimalPlaces)
    };

    const failures = [];
    if (!isValidXPolygon(finalXPolygon, failures)) {
      logApiError({
        args:[intXPolygon1,intXPolygon2], funcName, library,
        errorMessage:'API returned invalid XPolygon: ' + failures.join()
      });
      return undefined;
    }

    return finalXPolygon;
  } catch (error) { // Any exceptions thrown here are just ordinary debug errors, but follow the guarded style of logging errors and returning undefined to indicate failure.
    workerLogError({message:'Unexpected error inside of ' + funcName, error, context:{intXPolygon1, intXPolygon2}});
    return undefined;
  }
};

export const intersect = (xPolygon1, xPolygon2) =>
  _executeBooleanOperation({xPolygon1, xPolygon2, clipType:ClipperLib.ClipType.ctIntersection});

export const diff = (xPolygon1, xPolygon2) =>
  _executeBooleanOperation({xPolygon1, xPolygon2, clipType:ClipperLib.ClipType.ctDifference});

export const union = (xPolygon1, xPolygon2) =>
  _executeBooleanOperation({xPolygon1, xPolygon2, clipType:ClipperLib.ClipType.ctUnion});
